import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { StatCard } from "@/components/app/StatCard";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, ArrowRight, ShieldAlert, AlertTriangle, Smile, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { PushBanner } from "@/components/app/PushNotificationControls";
import UpcomingShiftsCard from "@/components/app/UpcomingShiftsCard";
import ConductBoard from "@/components/app/ConductBoard";
import RecentMoments from "@/components/app/RecentMoments";
import DepartmentRecognition from "@/components/app/DepartmentRecognition";
import ProactivityRecommendations from "@/components/app/ProactivityRecommendations";
import OverdueTicketsRecommendation from "@/components/app/OverdueTicketsRecommendation";
import AttendanceLiveSummary from "@/components/app/AttendanceLiveSummary";
import MaintenanceLoadByCategory from "@/components/app/MaintenanceLoadByCategory";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip } from "recharts";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const CHART_TICK = { fontSize: 12, fill: "#71717A" };
const CHART_CURSOR = { fill: "#F4F4F5" };
const CHART_TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E4E4E7" };
const CHART_BAR_RADIUS = [4, 4, 0, 0];

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedId, selected } = useProperty();
  const [summary, setSummary] = useState(null);
  const [byProperty, setByProperty] = useState([]);
  const [byZone, setByZone] = useState([]);
  const [byStaff, setByStaff] = useState([]);
  const [conduct, setConduct] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    if (user.role === "housekeeping" || user.role === "maintenance") return;
    try {
      if (user.role === "super_admin") {
        const [s, bp, bs] = await Promise.all([
          api.get(`/reports/summary`),
          api.get(`/reports/by-property`),
          api.get(`/reports/by-staff`),
        ]);
        setSummary(s.data); setByProperty(bp.data); setByStaff(bs.data);
      } else if (selectedId) {
        const [s, bz, bs] = await Promise.all([
          api.get(`/reports/summary?property_id=${selectedId}`),
          api.get(`/reports/by-zone?property_id=${selectedId}`),
          api.get(`/reports/by-staff?property_id=${selectedId}`),
        ]);
        setSummary(s.data); setByZone(bz.data); setByStaff(bs.data);
      }
      // Conduct breakdown — silently skip if user lacks perm
      try {
        const cb = await api.get(
          user.role === "super_admin"
            ? "/reports/conduct-breakdown"
            : `/reports/conduct-breakdown?property_id=${selectedId || ""}`
        );
        setConduct(cb.data);
      } catch (e) {
        // Expected for users without report perms — log for diagnostics only
        if (e?.response?.status !== 403) {
          logger.warn("Conduct breakdown fetch failed:", e);
        }
      }
    } catch (e) {
      // staff/restricted users may not have access to all report endpoints — log and ignore
      if (e?.response?.status !== 403) logger.error("Dashboard load failed", e);
    }
  }, [user, selectedId]);

  useEffect(() => { load(); }, [load]);

  const generateTasks = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/generate?property_id=${selectedId}`);
      toast.success(`Generated ${data.created} tasks for today`);
      load();
    } catch (e) {
      toast.error("Failed to generate tasks");
    } finally { setBusy(false); }
  };

  const isStaff = user?.role === "housekeeping" || user?.role === "maintenance";

  if (isStaff) return <StaffDashboard />;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="label-overline">{user?.role === "super_admin" ? "Company Overview" : `Property · ${selected?.location || ""}`}</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">
            {user?.role === "super_admin" ? "All Properties" : (selected?.name || "Dashboard")}
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Hello, {user?.name}. Here's your operations snapshot.</p>
        </div>
        {user?.role !== "super_admin" && (
          <Button onClick={generateTasks} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="generate-tasks-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Today's Tasks
          </Button>
        )}
      </div>

      {/* Conduct widget */}
      <PushBanner />
      <AttendanceLiveSummary />
      <MaintenanceLoadByCategory />
      <UpcomingShiftsCard />
      <ProactivityRecommendations />
      <OverdueTicketsRecommendation />
      <RecentMoments />
      <ConductBoard />
      <DepartmentRecognition />

      {conduct && conduct.total > 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="conduct-widget">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <span className="label-overline">Conduct</span>
              <h3 className="font-display text-xl font-bold tracking-tight mt-0.5">Team conduct snapshot</h3>
              <p className="text-xs text-zinc-500">Where everyone stands today — click a tile to filter the Staff list.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/staff" className="block rounded-md border border-red-200 bg-red-50/60 p-4 hover:shadow-sm transition" data-testid="conduct-tile-hard">
              <div className="flex items-center gap-2 text-red-700">
                <ShieldAlert className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest font-bold">Hard warnings</span>
              </div>
              <div className="font-display text-3xl font-black tracking-tight mt-1">{conduct.tally.hard}</div>
            </Link>
            <Link to="/staff" className="block rounded-md border border-orange-200 bg-orange-50/60 p-4 hover:shadow-sm transition" data-testid="conduct-tile-soft">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest font-bold">Soft warnings</span>
              </div>
              <div className="font-display text-3xl font-black tracking-tight mt-1">{conduct.tally.soft}</div>
            </Link>
            <Link to="/staff" className="block rounded-md border border-emerald-200 bg-emerald-50/60 p-4 hover:shadow-sm transition" data-testid="conduct-tile-appreciation">
              <div className="flex items-center gap-2 text-emerald-700">
                <Smile className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest font-bold">Appreciated</span>
              </div>
              <div className="font-display text-3xl font-black tracking-tight mt-1">{conduct.tally.appreciation}</div>
            </Link>
            <Link to="/staff" className="block rounded-md border border-zinc-200 bg-zinc-50/60 p-4 hover:shadow-sm transition" data-testid="conduct-tile-neutral">
              <div className="flex items-center gap-2 text-zinc-700">
                <Users className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest font-bold">Neutral</span>
              </div>
              <div className="font-display text-3xl font-black tracking-tight mt-1">{conduct.tally.none}</div>
            </Link>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {user?.role === "super_admin" && summary && (
          <StatCard label="Properties" value={summary.properties_count} testid="kpi-properties" />
        )}
        <StatCard label="Tasks Today" value={summary?.total_today ?? "—"} sub={`${summary?.completed_today ?? 0} done`} testid="kpi-total-today" />
        <StatCard label="Completion" value={`${summary?.completion_pct ?? 0}%`} accent="green" testid="kpi-completion" />
        <StatCard label="Pending" value={summary?.pending_today ?? 0} accent="amber" testid="kpi-pending" />
        <StatCard label="Overdue" value={summary?.overdue ?? 0} accent="red" testid="kpi-overdue" />
        <StatCard label="Open Tickets" value={summary?.open_tickets ?? 0} accent="amber" testid="kpi-tickets" />
        <StatCard label="Issues Today" value={summary?.issues ?? 0} accent="red" testid="kpi-issues" />
        {user?.role !== "super_admin" && (
          <StatCard label="Readiness" value={`${summary?.readiness ?? 0}%`} accent={summary?.readiness >= 80 ? "green" : summary?.readiness >= 60 ? "amber" : "red"} testid="kpi-readiness" />
        )}
      </div>

      {/* Charts row */}
      {user?.role === "super_admin" && byProperty.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <span className="label-overline">Property-wise Performance</span>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Completion by property</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={byProperty}>
              <XAxis dataKey="name" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} />
              <Tooltip cursor={CHART_CURSOR} contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="completion_pct" fill="#09090B" radius={CHART_BAR_RADIUS}>
                {byProperty.map((p) => <Cell key={p.property_id} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {user?.role !== "super_admin" && byZone.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <span className="label-overline">Zone Performance</span>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Completion by zone</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={byZone}>
              <XAxis dataKey="name" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} />
              <Tooltip cursor={CHART_CURSOR} contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="completion_pct" fill="#09090B" radius={CHART_BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {byStaff.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="label-overline">Staff Performance</span>
              <h3 className="font-display text-xl font-bold tracking-tight mt-1">Top contributors</h3>
            </div>
            <Link to="/reports" className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1" data-testid="see-all-reports">
              See reports <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-zinc-500 uppercase text-[10px] tracking-widest">
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Role</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-right py-2">Completed</th>
                  <th className="text-right py-2">Issues</th>
                  <th className="text-right py-2">%</th>
                </tr>
              </thead>
              <tbody>
                {byStaff.slice(0, 8).map((s) => (
                  <tr key={s.user_id} className="border-b border-zinc-100" data-testid={`staff-row-${s.user_id}`}>
                    <td className="py-2.5">{s.name}</td>
                    <td className="py-2.5 text-zinc-500 capitalize">{s.role.replace("_", " ")}</td>
                    <td className="py-2.5 text-right font-mono">{s.total}</td>
                    <td className="py-2.5 text-right font-mono">{s.completed}</td>
                    <td className="py-2.5 text-right font-mono text-red-600">{s.issues}</td>
                    <td className="py-2.5 text-right font-mono font-semibold">{s.completion_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffDashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    (async () => {
      const t = await api.get("/tasks/my");
      setTasks(t.data);
      const tk = await api.get("/tickets");
      setTickets(tk.data);
    })();
  }, []);

  const pending = tasks.filter((t) => t.status === "pending" || t.status === "overdue");
  const done = tasks.filter((t) => t.status === "completed");

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Hi, {user?.name?.split(" ")[0]}</span>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Today</h1>
      </div>
      <AttendanceLiveSummary />
      <UpcomingShiftsCard />
      <ProactivityRecommendations />
      <OverdueTicketsRecommendation />
      <RecentMoments />
      <ConductBoard />
      <DepartmentRecognition />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Open" value={pending.length} accent="amber" testid="staff-open" />
        <StatCard label="Done" value={done.length} accent="green" testid="staff-done" />
        <StatCard label="Tickets" value={tickets.length} testid="staff-tickets" />
      </div>
      <Link to="/my-tasks" className="block bg-zinc-950 text-white rounded-md p-6 hover:bg-zinc-800 transition-colors" data-testid="staff-go-to-tasks">
        <div className="label-overline text-zinc-500">My Tasks</div>
        <div className="font-display text-2xl font-bold tracking-tight mt-1">View all tasks →</div>
      </Link>
    </div>
  );
}
